import React, { useEffect, useState, useCallback, useRef } from 'react';
import SEO from "../component/SEO";
import { useSelector, useDispatch } from 'react-redux';
import { setGallery, addMoreGallery, setLoading, removeGalleryImage, updateImagePriorityInState } from '../redux/gallerySlice';
import { toast } from 'react-hot-toast';
import { Trash2, Maximize2, X, ChevronDown, Sparkles, Edit2, Check, XCircle } from 'lucide-react';
import LazyImage from '../components/LazyImage';

// Memoized Gallery Item to prevent unnecessary re-renders
const GalleryItem = React.memo(({ item, isAdmin, editingPriority, tempPriority, setTempPriority, handleSavePriority, handleCancelEdit, handleStartEdit, handleDelete, setSelectedImage }) => {
    return (
        <div 
            className="group relative aspect-square bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-zoom-in"
            onClick={() => setSelectedImage(item.imageUrl)}
        >
            <LazyImage 
                src={item.imageUrl} 
                alt="Gallery item" 
                className="w-full h-full"
            />
            
            {/* Priority Badge for Admins */}
            {isAdmin && (
                <div 
                    className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur shadow-sm rounded-full px-3 py-1 flex items-center gap-2 border border-orange-100"
                    onClick={(e) => e.stopPropagation()}
                >
                    {editingPriority === item._id ? (
                        <div className="flex items-center gap-1">
                            <input 
                                type="number"
                                value={tempPriority}
                                onChange={(e) => setTempPriority(e.target.value)}
                                className="w-12 text-xs p-1 border rounded outline-none focus:ring-1 focus:ring-orange-400"
                                autoFocus
                            />
                            <button onClick={(e) => handleSavePriority(e, item._id)} className="text-green-600"><Check size={14} /></button>
                            <button onClick={handleCancelEdit} className="text-red-500"><XCircle size={14} /></button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase">P: {item.priority}</span>
                            <button onClick={(e) => handleStartEdit(e, item)} className="text-slate-400 hover:text-orange-500"><Edit2 size={10} /></button>
                        </div>
                    )}
                </div>
            )}

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                <div className="bg-white/90 p-3 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <Maximize2 className="w-5 h-5 text-slate-700" />
                </div>
                
                {isAdmin && (
                    <button 
                        onClick={(e) => handleDelete(e, item._id)}
                        className="bg-red-500/90 p-3 rounded-full shadow-lg text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75 hover:bg-red-600"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                )}
            </div>
        </div>
    );
});

const Gallery = () => {
    const dispatch = useDispatch();
    const { galleryList, page, hasMore, loading } = useSelector(state => state.gallery);
    const user = useSelector(state => state.user);
    const [selectedImage, setSelectedImage] = useState(null);
    const [editingPriority, setEditingPriority] = useState(null); // id of image being edited
    const [tempPriority, setTempPriority] = useState("");
    
    // Sentinel for Infinite Scroll
    const loadMoreRef = useRef(null);
    
    const isAdmin = user?.isAdmin;

    const fetchGallery = useCallback(async (pageNum = 1) => {
        dispatch(setLoading(true));
        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/gallery?page=${pageNum}&limit=12`);
            const data = await response.json();
            if (response.ok) {
                if (pageNum === 1) {
                    dispatch(setGallery(data));
                } else {
                    dispatch(addMoreGallery(data));
                }
            } else {
                toast.error(data.message || "Failed to fetch gallery");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error connecting to server");
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch]);

    useEffect(() => {
        if (galleryList.length === 0) {
            fetchGallery(1);
        }
    }, [fetchGallery, galleryList.length]);

    const handleLoadMore = useCallback(() => {
        if (hasMore && !loading) {
            fetchGallery(page + 1);
        }
    }, [hasMore, loading, page, fetchGallery]);

    // Intersection Observer for Infinite Scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    handleLoadMore();
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
        };
    }, [handleLoadMore, hasMore, loading]);

    const handleDelete = useCallback(async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this image?")) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/gallery/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                }
            });
            const data = await response.json();
            if (response.ok) {
                toast.success(data.message);
                dispatch(removeGalleryImage(id));
            } else {
                toast.error(data.message || "Delete failed");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred");
        }
    }, [dispatch]);

    const handleStartEdit = useCallback((e, item) => {
        e.stopPropagation();
        setEditingPriority(item._id);
        setTempPriority(item.priority);
    }, []);

    const handleCancelEdit = useCallback((e) => {
        e.stopPropagation();
        setEditingPriority(null);
    }, []);

    const handleSavePriority = useCallback(async (e, id) => {
        e.stopPropagation();
        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/gallery/${id}/priority`, {
                method: "PATCH",
                headers: {
                    "content-type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                },
                body: JSON.stringify({ priority: tempPriority })
            });

            const data = await response.json();
            if (response.ok) {
                toast.success(data.message);
                dispatch(updateImagePriorityInState({ id, priority: parseInt(tempPriority) }));
                setEditingPriority(null);
            } else {
                toast.error(data.message || "Update failed");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred");
        }
    }, [dispatch, tempPriority]);

    // Memoize the sorted list to prevent re-calculations
    // Ensure we are not creating a new array if galleryList hasn't changed
    const sortedGalleryList = React.useMemo(() => {
        // Create a shallow copy before sorting to avoid mutating read-only state from Redux (if applicable)
        return [...galleryList].sort((a, b) => {
            if (a.priority === b.priority) {
                 // Fallback to _id or createdAt for stable sort
                 const dateA = new Date(a.createdAt || 0).getTime();
                 const dateB = new Date(b.createdAt || 0).getTime();
                 if (dateA !== dateB) return dateB - dateA; // Newest first
                 
                 return (a._id || "").localeCompare(b._id || "");
            }
            return a.priority - b.priority;
        });
    }, [galleryList]);

    // Optimize callbacks to ensure passing stable functions to GalleryItem
    const handleStartEditMemo = useCallback((e, item) => handleStartEdit(e, item), [handleStartEdit]);
    const handleCancelEditMemo = useCallback((e) => handleCancelEdit(e), [handleCancelEdit]);
    const handleSavePriorityMemo = useCallback((e, id) => handleSavePriority(e, id), [handleSavePriority]);
    const handleDeleteMemo = useCallback((e, id) => handleDelete(e, id), [handleDelete]);
    const setSelectedImageMemo = useCallback((img) => setSelectedImage(img), []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-pink-50 via-orange-50 to-purple-50 pb-20 pt-10">
            <div className="container mx-auto px-4">
                <SEO title="Gallery - K Bakery" description="View our gallery of delicious cakes and past creations." />
                {/* Header Section */}
                <div className="text-center mb-12 space-y-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full border border-pink-100 shadow-sm">
                        <Sparkles className="w-4 h-4 text-pink-400" />
                        <span className="text-sm font-medium text-slate-600">Our Sweet Moments</span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-serif text-slate-800">Gallery</h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Explore our delicious journey through these captured moments of joy and sweetness.
                        {isAdmin && <span className="block mt-2 text-xs font-bold text-orange-500 uppercase tracking-widest">Admin Mode: Sorting by Priority (1 = Highest)</span>}
                    </p>
                </div>

                {/* Gallery Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {sortedGalleryList.map((item, index) => (
                        <GalleryItem 
                            key={item._id || `gallery-item-${index}`}
                            item={item}
                            isAdmin={isAdmin}
                            editingPriority={editingPriority}
                            tempPriority={tempPriority}
                            setTempPriority={setTempPriority}
                            handleSavePriority={handleSavePriorityMemo}
                            handleCancelEdit={handleCancelEditMemo}
                            handleStartEdit={handleStartEditMemo}
                            handleDelete={handleDeleteMemo}
                            setSelectedImage={setSelectedImageMemo}
                        />
                    ))}
                </div>

                {/* Empty State */}
                {!loading && galleryList.length === 0 && (
                    <div className="text-center py-20 bg-white/30 backdrop-blur-md rounded-3xl border border-white/50 shadow-sm">
                        <p className="text-slate-500 text-xl font-medium">No images in the gallery yet.</p>
                    </div>
                )}

                {/* Loading State & Infinite Scroll Sentinel */}
                <div ref={loadMoreRef} className="flex justify-center mt-12 h-20">
                    {loading && (
                        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
                    )}
                </div>

                {/* Fallback Load More Button (if observer fails or for accessibility) */}
                {!loading && hasMore && galleryList.length > 0 && (
                    <div className="flex justify-center -mt-8 pb-10">
                        <button 
                            onClick={handleLoadMore}
                            className="text-slate-400 hover:text-orange-500 text-sm font-medium transition-colors"
                        >
                           Load More
                        </button>
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <button 
                        className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={40} />
                    </button>
                    <img 
                        src={selectedImage} 
                        alt="Gallery preview" 
                        className="max-w-full max-h-[90vh] object-contain rounded-lg animate-in zoom-in-95 duration-300"
                    />
                </div>
            )}
        </div>
    );
};

export default Gallery;
